/** Storage access for transliterated PHP arrays; all key/coercion/copy semantics
 * belong to php-compat. This accepts native page JSON and ordered referee inputs. */
import { PhpArray, fromJson, phpCopy, type PhpKeyInput } from '../../php-compat';
import {portWithWrites,portWritesBrand} from '../../port-contract';

/** C0 cache/write-back effects are domain intents rather than inline SQL. Keep
 * the PHP return independent while using the contract's comparison brand. */
export interface WithIntents<T,I> {readonly [portWritesBrand]:true;value:T;writes:I[];}
export function withIntents<T,I>(value:T,writes:I[]):WithIntents<T,I> {
    return {...portWithWrites(value,[]),writes};
}

/** A writer callback still returns the native PHP value to its caller. Retain
 * its ordered intents exactly once before using that value in another call. */
export function appendDependencyWrites<I>(result: unknown, writes: I[]): unknown {
    if (result === null || typeof result !== 'object' || !(portWritesBrand in result)) return result;
    const writer = result as WithIntents<unknown, I>;
    if (writer[portWritesBrand] !== true || !Array.isArray(writer.writes))
        throw new TypeError('Invalid PortWithWrites dependency');
    writes.push(...writer.writes);
    return writer.value;
}

export function isArray(value: unknown): value is PhpArray | unknown[] | Record<string, unknown> {
    return value instanceof PhpArray || Array.isArray(value)
        || value !== null && typeof value === 'object' && (Object.getPrototypeOf(value) === null
            || Object.prototype.toString.call(value) === '[object Object]'
                && Object.getPrototypeOf(Object.getPrototypeOf(value)) === null);
}
export function array(value: unknown): PhpArray {
    if (value instanceof PhpArray) return value;
    if (isArray(value)) return fromJson(value) as PhpArray;
    return new PhpArray();
}
/** PHP foreach accepts Traversable as well as arrays. Keep this separate from
 * array()/isArray(): a lazy provider is not a PHP array, and must not disappear
 * merely because its callback returns an iterator instead of a materialized list. */
export function values(value: unknown): Iterable<unknown> {
    if (isArray(value)) return array(value).values();
    if (value !== null && typeof value === 'object'
        && typeof (value as { [Symbol.iterator]?: unknown })[Symbol.iterator] === 'function')
        return value as Iterable<unknown>;
    return [];
}
export function get(value: unknown, ...keys: PhpKeyInput[]): unknown {
    for (const key of keys) {
        if (!isArray(value)) return undefined;
        value = array(value).get(key);
    }
    return value;
}
export function put(target: PhpArray, keys: PhpKeyInput[], value: unknown): null {
    let current = target;
    for (const key of keys.slice(0, -1)) {
        let child = current.get(key);
        if (!(child instanceof PhpArray)) { child = array(child); current.set(key, child); }
        current = child as PhpArray;
    }
    current.set(keys[keys.length - 1]!, phpCopy(value));

 return null;
}
export function remove(target: PhpArray, ...keys: PhpKeyInput[]): null {
    const parent = keys.length === 1 ? target : get(target, ...keys.slice(0, -1));
    if (parent instanceof PhpArray) parent.delete(keys[keys.length - 1]!);

 return null;
}
export const record = (entries: Record<string, unknown>): PhpArray => fromJson(entries) as PhpArray;
export const list = (...values: unknown[]): PhpArray => fromJson(values) as PhpArray;
